import { Component, inject} from '@angular/core';
import {ICellRendererAngularComp} from "ag-grid-angular";
import {ICellRendererParams} from "ag-grid-community";
import { ConfigurationManagerService } from 'src/app/core';
import { EInvoicesStatus, HistoryModuleSections } from 'src/app/features/history/models/history.module';
import { HistoryDataOperationsService } from 'src/app/features/history/services/history-data-operations.service';
import { Sales } from 'tilby-models';

export interface CliccableActionSales<T> {
  action: 'details' | 'send-pms' | 'show-json'; 
  data: T;
}
export interface CliccableActionEInvoices<T> {
  action: 'send-e-invoice' | 'edit-customer' |
    'edit-invoice-options' | 'duplicate-sale' |
    'change-sale-status' | 'download-e-invoice' |
    'send-via-email-e-invoice' | 'open-help-message'; 
  data: T;
}
export interface HistoryGridClickableConfig {
  section?: HistoryModuleSections;
}
@Component({
	standalone: false,
    selector: 'app-grid-clickable-history-sales-button',
    templateUrl: './grid-clickable-history-sales-button.component.html',
    styleUrls: ['./grid-clickable-history-sales-button.component.scss'],
})
export class GridClickableHistorySalesButtonComponent implements ICellRendererAngularComp {
    private historyDataOperationsService = inject(HistoryDataOperationsService);
    private configurationManagerService = inject(ConfigurationManagerService);
    protected isExpenseReportEnabled = !!(this.configurationManagerService.getPreference('cashregister.expense_report.department_id') && this.configurationManagerService.getPreference('cashregister.expense_report.template_id'));

    protected params!: ICellRendererParams;
    private sale?: Sales;

    protected isSaleSection = false;
    protected isEInvoice = false;
    protected isCreditNote = false;
    protected canUseExpenseReport = false;
    protected isSendPmsActive = false;

    protected isSalesDataSentSection = false;
    protected canSendPms = false;
    protected hasSalesDataSent = false;
    protected canShowJson = false;

    protected isEInvoiceSection = false;
    protected eInvoicesStatus: EInvoicesStatus = 'error';
    protected canEditState: boolean = false;

    agInit(params: ICellRendererParams): void {
        this.params = params;
        this.initData(params);
    }

    refresh(params: ICellRendererParams): boolean {
        this.agInit(params);
        return true;
    }

    private initData(params: ICellRendererParams): void {
        this.sale = params.data as Sales;

        const config: HistoryGridClickableConfig = (params as any).section !== undefined
            ? (params as any) 
            : params.colDef?.cellRendererParams;

        if(config.section) {
            if(config.section == 'sales') {
                this.isSaleSection = true;
                this.isEInvoice = !!this.sale.e_invoice?.invoice_progressive;
                this.isCreditNote = this.sale.final_amount! < 0;
                this.canUseExpenseReport = this.isExpenseReportEnabled && !this.sale.sale_documents?.some((doc) => doc.document_type == 'expense_report')
            } else if (config.section == 'sales_data_sent') {
                this.isSalesDataSentSection = true;

                const massiveSendStatus = params.context?.massiveSendStatus;
                const canShowDeveloperOptions = params.context?.canShowDeveloperOptions;

                const displayStatus = this.historyDataOperationsService.getSaleDataSentStatus(this.sale, massiveSendStatus);
                this.canSendPms = 
                    (displayStatus !== 'pending') && 
                    (displayStatus !== 'closed' || canShowDeveloperOptions)
                this.canShowJson = canShowDeveloperOptions;

                this.hasSalesDataSent = !!(this.sale as any).sale_data_sent?.length;
            } else if (config.section == 'e_invoices') {
                this.isEInvoiceSection = true;
                this.eInvoicesStatus = params.context?.eInvoicesStatus;

                const sendMode = this.configurationManagerService.getSetting("e_invoice.send_mode") || '';
                this.canEditState = ['download', 'ftp'].includes(sendMode);
            }
        }
    }

    protected get isSending(): boolean {
        if (this.params?.context?.isSending && this.sale?.id) {
            return this.params.context.isSending(this.sale.id);
        }
        return false;
    }

    onOpenDetail($event: any) {
        this.params.context.componentParent.clickableAction.emit({
            action: 'detail',
            data: this.sale,
            node: this.params.node
        });
    }

    onSendPms($event: any) {
        this.params.context.componentParent.clickableAction.emit({
            action: 'send-pms',
            data: this.sale
        });
    }

    onShowJson($event: any) {
        this.params.context.componentParent.clickableAction.emit({
            action: 'show-json',
            data: this.sale
        });
    }

    onRefundVoid($event: any) {
        this.params.context.componentParent.clickableAction.emit({
            action: 'history-sale-credit-note-cancel',
            data: this.sale
        });
    }

    onRestoreSale($event: any) {
        this.params.context.componentParent.clickableAction.emit({
            action: 'history-sale-restore',
            data: this.sale
        });
    }

    onCourtesyReceipt($event: any) {
        this.params.context.componentParent.clickableAction.emit({
            action: 'history-sale-credit-print-courtesy-receipt',
            data: this.sale
        });
    }

    onDuplicateSale($event: any) {
        this.params.context.componentParent.clickableAction.emit({
            action: 'duplicate-sale',
            data: this.sale
        });
    }

    onCreateExpenseReport($event: any) {
        this.params.context.componentParent.clickableAction.emit({
            action: 'history-sale-expense-report',
            data: this.sale
        });
    }

    sendEInvoice($event: any) {
        this.params.context.componentParent.clickableAction.emit({
            action: 'send-e-invoice',
            data: this.sale
        });
    }

    editCustomer($event: any) {
        this.params.context.componentParent.clickableAction.emit({
            action: 'edit-customer',
            data: this.sale
        });
    }

    editInvoiceOptions($event: any) {
        this.params.context.componentParent.clickableAction.emit({
            action: 'edit-invoice-options',
            data: this.sale
        });
    }

    duplicateSale($event: any) {
        this.params.context.componentParent.clickableAction.emit({
            action: 'duplicate-sale',
            data: this.sale
        });
    }

    changeSaleStatus($event: any) {
        this.params.context.componentParent.clickableAction.emit({
            action: 'change-sale-status',
            data: this.sale
        });
    }

    downloadEInvoice($event: any) {
        this.params.context.componentParent.clickableAction.emit({
            action: 'download-e-invoice',
            data: this.sale
        });
    }

    sendViaEmailEInvoice($event: any) {
        this.params.context.componentParent.clickableAction.emit({
            action: 'send-via-email-e-invoice',
            data: this.sale
        });
    }

    openHelpMessage($event: any) {
        this.params.context.componentParent.clickableAction.emit({
            action: 'open-help-message',
            data: this.sale
        });
    }

    preventSelection($event: MouseEvent) {
        $event.stopPropagation();
    }
}
